import { isNil } from 'lodash';
import { MouseEvent, useState } from 'react';
import { StyleProp, ViewStyle } from 'react-native';
import { Button } from '@shared/components/Button';
import { View } from '@shared/components/View';
import { CollectionViewAnalytics } from '@shared/scenes/cards/collectionView/CollectionViewAnalytics';
import {
  SingleComponentForm,
  SingleComponentFormProps,
} from '@shared/scenes/cards/singleComponentForms/SingleComponentForm';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { getAnchorName } from '@shared/styles';
import { API } from '@shared/types/api';
import { CardCollectionViewFieldContainer } from '../../fields/CardCollectionViewFieldContainer';
import { WorkflowEntityProvider } from './provider';
import { useField, useLoadingAndError, useUpdateField } from './workflowEntityHooks';

interface InputButtonProps {
  component: API.InputButtonComponent;
  style: StyleProp<ViewStyle>;
  workflowTemplateId: string;
  entityId: string;
  analytics?: CollectionViewAnalytics;
  canUpdateCard?: boolean;
  paddingTop?: number;
}

export function InputButton(props: InputButtonProps) {
  if (!props.canUpdateCard) {
    return null;
  }

  return <InputButtonContent {...props} />;
}

function InputButtonContent({
  component,
  style,
  workflowTemplateId,
  entityId,
  analytics,
  paddingTop = 0,
}: InputButtonProps) {
  const anchorName = getAnchorName('input-button', entityId, component.inputComponentId);
  const [showForm, setShowForm] = useState(false);
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);

  const onClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    analytics?.trackButtonClick(entityId);
    setShowForm(prev => !prev);
  };

  return (
    <CardCollectionViewFieldContainer style={[style, { paddingTop }]}>
      <View row>
        <Button
          variant="secondary"
          label={component.buttonText}
          numberOfLines={1}
          onClick={onClick}
          style={{ anchorName }}
          size="medium"
        />
      </View>
      {showForm && !isNil(workflowTemplate) && (
        <WorkflowEntityProvider entityId={entityId}>
          <InputButtonForm
            fieldName={component.inputComponentId}
            anchorName={anchorName}
            onClose={() => setShowForm(false)}
            workflowTemplate={workflowTemplate}
          />
        </WorkflowEntityProvider>
      )}
    </CardCollectionViewFieldContainer>
  );
}

type InputButtonFormProps = Omit<SingleComponentFormProps, 'field' | 'loading' | 'onUpdateField'>;

function InputButtonForm({
  fieldName,
  anchorName,
  workflowTemplate,
  onClose,
}: InputButtonFormProps) {
  const { loading } = useLoadingAndError();
  const field = useField(fieldName);
  const onUpdateField = useUpdateField();

  return (
    <SingleComponentForm
      fieldName={fieldName}
      field={field}
      anchorName={anchorName}
      workflowTemplate={workflowTemplate}
      onClose={onClose}
      loading={loading}
      onUpdateField={onUpdateField}
    />
  );
}
